import React, { useState } from 'react';

import { IIndicador } from '../../../../../../hooks/useFechamentoContabilMensal';
import {
  FORMULA_DO_INDICADOR,
  GRUPO_DE_INDICADOR,
  leituraDoIndicador,
  mesAno,
  nomeDoIndicador,
  valorDoIndicador,
} from '../../regrasDoDetalhe';

const SECAO: React.CSSProperties = {
  minWidth: 0,
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  background: 'rgba(255, 255, 255, .62)',
  border: '1px solid rgba(255, 255, 255, .8)',
  borderRadius: 16,
  boxShadow: '0 8px 28px rgba(21, 95, 176, .10)',
  backdropFilter: 'blur(14px)',
};
const ROTULO: React.CSSProperties = {
  fontSize: 11,
  color: '#52525B',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  fontWeight: 600,
};

interface IFaixa {
  min: number;
  max: number;
}

const faixaDe = (valores: number[]): IFaixa => {
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const folga = (max - min) * 0.15 || Math.abs(max) * 0.1 || 1;
  return { min: min - folga, max: max + folga };
};

const pontos = (vals: (number | null)[], f: IFaixa, w: number, h: number) =>
  vals
    .map((v, i) =>
      v == null
        ? null
        : `${(vals.length === 1 ? w / 2 : (i / (vals.length - 1)) * w).toFixed(1)},${(
            h -
            ((v - f.min) / (f.max - f.min)) * h
          ).toFixed(1)}`,
    )
    .filter(Boolean)
    .join(' ');

function Cartao({
  indicador,
  ativo,
  onEscolher,
}: {
  indicador: IIndicador;
  ativo: boolean;
  onEscolher: () => void;
}) {
  const { nome, valor, anterior, serie } = indicador;
  const validos = serie.map(s => s.valor).filter((v): v is number => v != null);
  const mesAnterior = serie.length > 1 ? mesAno(serie[serie.length - 2].competencia) : 'anterior';
  let variacao = 'sem mês anterior';
  if (valor != null && anterior != null) {
    if (valor === anterior) variacao = `igual a ${mesAnterior}`;
    else
      variacao = `${valor > anterior ? '▲' : '▼'} ${mesAnterior}: ${valorDoIndicador(nome, anterior)}`;
  }
  return (
    <button
      type="button"
      className="cartaoInd"
      onClick={onEscolher}
      aria-pressed={ativo}
      style={{
        textAlign: 'left',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        cursor: 'pointer',
        background: 'rgba(255, 255, 255, .62)',
        border: `1px solid ${ativo ? '#1B88F5' : 'rgba(255, 255, 255, .8)'}`,
        borderRadius: 16,
        boxShadow: '0 8px 28px rgba(21, 95, 176, .10)',
        backdropFilter: 'blur(14px)',
        color: '#0F172A',
        font: 'inherit',
      }}
    >
      <span style={{ fontSize: 11.5, color: '#52525B', fontWeight: 600 }}>
        {nomeDoIndicador(nome)}
      </span>
      <b style={{ fontSize: 22, color: valor == null ? '#94A3B8' : '#0F172A' }}>
        {valor == null ? 'Sem dado' : valorDoIndicador(nome, valor)}
      </b>
      <span style={{ fontSize: 11.5, color: '#52525B' }}>{variacao}</span>
      {validos.length > 1 && (
        <svg
          viewBox="0 0 120 30"
          preserveAspectRatio="none"
          aria-hidden="true"
          style={{ width: '100%', height: 30 }}
        >
          <polyline
            points={pontos(
              serie.map(s => s.valor),
              faixaDe(validos),
              120,
              30,
            )}
            fill="none"
            stroke="#1B88F5"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
      {indicador.distorcido_por.length > 0 && (
        <span style={{ fontSize: 11, color: '#B45309' }}>
          Atenção: há constatação que afeta este número
        </span>
      )}
    </button>
  );
}

/**
 * Indicadores no layout de 10/2026: grupo, cartões com a série do período e o gráfico
 * do escolhido com a média do período. Os números vêm da revisão de cada mês.
 */
export function AbaIndicadores({
  indicadores,
  periodoTexto,
}: {
  indicadores: IIndicador[];
  /** "jan a set/2026" */
  periodoTexto: string;
}) {
  const grupos = Array.from(new Set(indicadores.map(i => i.grupo)));
  const [grupoPedido, setGrupo] = useState('');
  const [nomePedido, setNome] = useState('');
  const grupo = grupos.includes(grupoPedido) ? grupoPedido : grupos[0];
  const lista = indicadores.filter(i => i.grupo === grupo);
  const sel = lista.find(i => i.nome === nomePedido) ?? lista[0];

  if (!sel)
    return (
      <div style={{ ...SECAO, alignItems: 'center', fontSize: 13, color: '#52525B' }}>
        Sem indicadores nesta competência.
      </div>
    );

  const fmt = (v: number | null | undefined) => valorDoIndicador(sel.nome, v);
  const valores = sel.serie.map(s => s.valor);
  const validos = valores.filter((v): v is number => v != null);
  const media = validos.length ? validos.reduce((a, b) => a + b, 0) / validos.length : null;
  const f = validos.length ? faixaDe(validos) : null;
  const linha = f ? pontos(valores, f, 600, 220) : '';
  const formula = FORMULA_DO_INDICADOR[sel.nome] || sel.formula;
  const ultimo = [...sel.serie].reverse().find(s => s.valor != null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        role="group"
        aria-label="Grupo de indicadores"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
      >
        {grupos.map(g => {
          const ativo = g === grupo;
          return (
            <button
              key={g}
              type="button"
              aria-pressed={ativo}
              onClick={() => {
                setGrupo(g);
                setNome('');
              }}
              style={{
                padding: '5px 14px',
                borderRadius: 999,
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${ativo ? '#1B88F5' : '#E4E4E7'}`,
                background: ativo ? '#E9F4FD' : '#FFFFFF',
                color: ativo ? '#155FB0' : '#52525B',
              }}
            >
              {GRUPO_DE_INDICADOR[g] ?? g}
            </button>
          );
        })}
      </div>

      <div className="cards4">
        {lista.map(i => (
          <Cartao
            key={i.nome}
            indicador={i}
            ativo={i.nome === sel.nome}
            onEscolher={() => setNome(i.nome)}
          />
        ))}
      </div>

      <div className="g21">
        <section aria-label={nomeDoIndicador(sel.nome)} style={SECAO}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
            {nomeDoIndicador(sel.nome)} · {periodoTexto}
          </h2>
          {f ? (
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                <div
                  style={{
                    width: 64,
                    height: 220,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    textAlign: 'right',
                    fontSize: 10.5,
                    color: '#52525B',
                  }}
                >
                  {[4, 3, 2, 1, 0].map(k => (
                    <span key={k}>{fmt(f.min + ((f.max - f.min) * k) / 4)}</span>
                  ))}
                </div>
                <div
                  style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <svg
                    viewBox="0 0 600 220"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label={nomeDoIndicador(sel.nome)}
                    style={{
                      width: '100%',
                      height: 220,
                      backgroundImage: 'linear-gradient(#E4E4E7 1px, transparent 1px)',
                      backgroundSize: '100% 55px',
                    }}
                  >
                    {validos.length === valores.length && validos.length > 1 && (
                      <polyline
                        points={`0,220 ${linha} 600,220`}
                        fill="rgba(27, 136, 245, .12)"
                        stroke="none"
                      />
                    )}
                    <polyline
                      points={linha}
                      fill="none"
                      stroke="#1B88F5"
                      strokeWidth="2.6"
                      vectorEffect="non-scaling-stroke"
                    />
                    {media != null && (
                      <polyline
                        points={pontos(
                          valores.map(() => media),
                          f,
                          600,
                          220,
                        )}
                        fill="none"
                        stroke="#94A3B8"
                        strokeWidth="1.6"
                        strokeDasharray="6 4"
                        vectorEffect="non-scaling-stroke"
                      />
                    )}
                  </svg>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 10.5,
                      color: '#52525B',
                    }}
                  >
                    {sel.serie.map(s => (
                      <span key={s.competencia}>{mesAno(s.competencia)}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center' }}>
                {[
                  [nomeDoIndicador(sel.nome), '#1B88F5', fmt(ultimo?.valor)],
                  ['Média do período', '#94A3B8', fmt(media)],
                ].map(([nome, c, v]) => (
                  <span
                    key={nome}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11.5,
                      color: '#52525B',
                    }}
                  >
                    <span style={{ width: 14, height: 3, borderRadius: 2, background: c }} />
                    {nome} · <b style={{ color: '#0F172A' }}>{v}</b>
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div
              style={{
                height: 220,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '0 40px',
                border: '1px dashed #CBD5E1',
                borderRadius: 12,
                fontSize: 13,
                color: '#52525B',
              }}
            >
              Sem valor deste indicador no período.
            </div>
          )}
        </section>
        <section aria-label="Leitura" style={{ ...SECAO, gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Leitura</h2>
          {leituraDoIndicador(sel.nome) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={ROTULO}>O que mede</span>
              <span style={{ fontSize: 12.5 }}>{leituraDoIndicador(sel.nome)}</span>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={ROTULO}>Como calcula</span>
            <span style={{ fontSize: 12.5 }}>{formula}</span>
          </div>
          {sel.leitura && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={ROTULO}>Neste mês</span>
              <span style={{ fontSize: 12.5 }}>{sel.leitura}</span>
            </div>
          )}
          {sel.distorcido_por.length > 0 && (
            <div
              style={{
                padding: '8px 12px',
                borderLeft: '3px solid #D97706',
                borderRadius: 6,
                background: 'rgba(15, 23, 42, .03)',
                fontSize: 12.5,
              }}
            >
              Distorcido por constatações abertas ({sel.distorcido_por.join(', ')}). Leia com
              cautela até os ajustes.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
